import { useEffect, useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from 'app/javascript/components/ui/toggle-group';
import { trackEvent } from 'app/javascript/firebase';
import type { StoreCategory } from 'app/javascript/data/storeCatalogs';

interface StoreCategoryNavProps {
  sections: Array<{ id: StoreCategory; label: string }>;
  companySlug: string;
}

// Sticky jump-nav for the store categories. ToggleGroup handles selected,
// keyboard, and focus states; IntersectionObserver keeps selection synced with
// the section currently under the sticky bars.
export default function StoreCategoryNav({ sections, companySlug }: StoreCategoryNavProps) {
  const [activeId, setActiveId] = useState<StoreCategory | null>(sections[0]?.id ?? null);

  useEffect(() => {
    if (sections.length === 0) return undefined;

    setActiveId(sections[0].id);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (visible?.target.id) setActiveId(visible.target.id as StoreCategory);
      },
      { rootMargin: '-140px 0px -55% 0px', threshold: 0 }
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [sections]);

  if (sections.length <= 1) return null;

  const handleJump = (id: StoreCategory) => {
    setActiveId(id);
    trackEvent('store_category_clicked', { company_slug: companySlug, category: id });
  };

  return (
    <nav className="sticky top-14 z-30 border-b border-border/40 bg-background/85 backdrop-blur-xl" aria-label="Categorias">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <ToggleGroup
          type="single"
          value={activeId ?? undefined}
          className="max-w-full justify-start gap-1 overflow-x-auto rounded-md py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {sections.map((section) => (
            <ToggleGroupItem
              key={section.id}
              value={section.id}
              asChild
              className="min-h-11 shrink-0 rounded-md border border-transparent bg-transparent px-3.5 text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground shadow-none hover:border-slate-700/60 hover:bg-slate-900/70 hover:text-slate-200 data-[state=on]:border-yellow-400/35 data-[state=on]:bg-slate-900 data-[state=on]:text-yellow-400 data-[state=on]:shadow-none"
            >
              <a
                href={`#${section.id}`}
                onClick={() => handleJump(section.id)}
              >
                {section.label}
              </a>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
    </nav>
  );
}
